/**
 * roleCatalog — business names, plain-language effects, and the per-user
 * "Effective access" summary for Identity → Users / Roles (Bill 2026-10-07).
 *
 * Every statement below is derived from where the code checks the role. File
 * references are given so the text can be re-verified when a gate moves:
 *
 *   Server (case-INSENSITIVE, SuperAdmin passes every check, no other
 *   hierarchy):  apps/PCBRetailApi/Services/AdminAuthService.cs HasRole/HasAnyRole.
 *   risk-mgmt-panel outer gate: RiskManager | SuperAdmin, EXACT spelling
 *     (apps/risk-mgmt-panel/src/components/RoleGate.tsx).
 *   sysops-panel gate: SystemOps | SuperAdmin, EXACT spelling
 *     (apps/sysops-panel/src/components/RoleGate.tsx).
 *   admin-panel gate: SuperAdmin | Admin | RetailAdmin | SysOpsManager |
 *     RiskManager | DisclosureManager, EXACT spelling (components/RoleGate.tsx).
 *   ROP-teams (officer app): primary role = first EXACT match in
 *     SuperAdmin > Admin > SystemOps > Risk > Executive > Manager > Supervisor
 *     > Officer (apps/ROP-teams/src/contexts/AuthContext.tsx pickPrimaryRole);
 *     the New Acct tab shows for primary role Officer | Admin | SuperAdmin
 *     (apps/ROP-teams/src/constants/modules.ts). Opening an account needs the
 *     Officer role on the server (identity verify, LexisNexis, e-sign, signing,
 *     card activation — AuthorizeAsync "Officer"), operator initials + number
 *     (CoreWriteIdentityService), and the person not in Sandbox mode (Users.IsInSandboxMode; CartModeResolver).
 *
 * Business names are Bill's (2026-10-07). "SysOps Admin" has no role code —
 * it is covered by Global Admin (SuperAdmin).
 */

/** Bill's business names for the catalog codes. Code stays the stable key. */
export const BUSINESS_NAMES: Readonly<Record<string, string>> = {
  officer: 'Officer',
  supervisor: 'Supervisor',
  manager: 'Manager',
  executive: 'Executive',
  admin: 'Retail Admin',
  risk: 'Risk Officer',
  riskmanager: 'Risk Admin',
  systemops: 'SysOps',
  superadmin: 'Global Admin',
};

/** Plain-language summary of what each role grants (derived from the gates). */
export const ROLE_GRANTS: Readonly<Record<string, string>> = {
  superadmin:
    'Everything, everywhere: passes every check in every app and panel, and is the only role that can remove a role, delete a user, or run Sync.',
  admin:
    'Retail operations settings in this admin panel (products, notifications, retention, users and roles — can add roles but not remove them). In the officer app: the Admin screens and every cart. Does NOT open accounts on its own (that needs Officer) and does NOT open the Risk panel.',
  officer:
    'Opens real accounts in the officer app: identity checks, LexisNexis, e-sign, signing, debit-card activation, follow-up tasks. Requires Core Director initials and an operator number on file. Sees their own and their branch\'s carts. Product categories below narrow which account types they can open.',
  supervisor:
    'Officer app: approvals, kill list, cart resolve/restore and the compliance queue for their group; can reassign carts. Workflow settings (pipelines, signature rules, task templates). Does NOT open accounts on its own.',
  manager:
    'Officer app: metrics and every cart in their region; can reassign carts. Reference settings (branches, regions, products, card types). Does NOT open accounts on its own.',
  executive:
    'Officer app: reporting and metrics only (no individual carts). Does NOT open accounts on its own.',
  risk:
    'Read-only view of compliance and disclosure settings on the server, and the prerequisite for Disclosure Manager. Does NOT open the Risk panel (that needs Risk Admin). In the officer app it outranks Officer when choosing the person\'s main role, which can leave their menu empty.',
  riskmanager:
    'Opens the Risk panel (risk.mypcb.com): compliance gating reviews, risk settings, disclosure submissions; edits all compliance rules. Also opens the Compliance, Entity Rules, Documents and Retention areas of this admin panel.',
  disclosuremanager:
    'Approves, rejects, activates and retires disclosure versions and edits document requirements. Needs Risk Officer first. Opens the Documents area of this admin panel; to reach the Risk panel screens the person also needs Risk Admin.',
  systemops:
    'Opens the SysOps panel: missing-initials queue, Core Director operator sync and name-mismatch fixes. In the officer app it outranks Officer when choosing the person\'s main role, which can leave their menu empty.',
  bsaofficer: 'No effect today — no app or route checks this role.',
};

export interface CatalogRole {
  code: string;
  name: string;
  isActive: boolean;
  sortOrder?: number | null;
  description?: string | null;
}

/** Display label: business name when Bill named it, else the catalog name. */
export function roleLabel(code: string, catalogName?: string | null): string {
  return BUSINESS_NAMES[code.toLowerCase()] ?? catalogName ?? code;
}

export function roleGrants(code: string, description?: string | null): string {
  return ROLE_GRANTS[code.toLowerCase()] ?? description ?? 'No documented effect.';
}

/** Case-insensitive lookup of a stored code in the catalog. */
export function canonicalCode(catalog: ReadonlyArray<CatalogRole>, stored: string): string {
  const hit = catalog.find((r) => r.code.toLowerCase() === stored.trim().toLowerCase());
  return hit ? hit.code : stored.trim();
}

/** Unique role codes, catalog spelling, case-insensitive dedupe. */
export function canonicalCodes(catalog: ReadonlyArray<CatalogRole>, stored: ReadonlyArray<string>): string[] {
  const out: string[] = [];
  for (const s of stored) {
    const c = canonicalCode(catalog, s);
    if (!out.some((o) => o.toLowerCase() === c.toLowerCase())) out.push(c);
  }
  return out;
}

const ROP_PRECEDENCE = ['SuperAdmin', 'Admin', 'SystemOps', 'Risk', 'Executive', 'Manager', 'Supervisor', 'Officer'] as const;

export interface AccessLine {
  area: string;
  verdict: 'yes' | 'no' | 'partial';
  detail: string;
}

export interface AccessSubject {
  isActive: boolean;
  entraLinked: boolean;
  officerCode: string | null;
  operatorNumber: string | null;
  missingOfficerCode: boolean;
  /** Users.IsInSandboxMode — the flag CartModeResolver reads (TrainingMode is not read by the server). */
  isInSandboxMode: boolean;
}

/**
 * Predicts what the person can do, from the role codes AS THEY WILL BE STORED.
 * `stored` must be the exact spellings (front-end gates are case-sensitive).
 */
export function effectiveAccess(stored: ReadonlyArray<string>, s: AccessSubject): AccessLine[] {
  const exact = new Set(stored.map((c) => c.trim()));
  const ci = new Set(stored.map((c) => c.trim().toLowerCase()));
  const hasExact = (...codes: string[]) => codes.some((c) => exact.has(c));
  const hasCi = (...codes: string[]) => codes.some((c) => ci.has(c.toLowerCase()));
  const lines: AccessLine[] = [];

  if (!s.isActive) {
    return [{ area: 'Everything', verdict: 'no', detail: 'Account is inactive — every role check fails until it is reactivated.' }];
  }

  const signIn = s.entraLinked
    ? null
    : 'Not linked to an Entra account yet — the Risk, SysOps and admin panels identify people by their Entra ID, so they will be refused until the link is made (Sync from Entra).';

  // Officer app
  const primary = ROP_PRECEDENCE.find((r) => exact.has(r)) ?? (stored.length > 0 ? null : 'Officer');
  const canServerOpen = hasCi('Officer', 'SuperAdmin');
  const hasOperator = !!(s.officerCode && s.operatorNumber) && !s.missingOfficerCode;
  const routedToSandbox = s.isInSandboxMode;
  const newAcctTab = primary === 'Officer' || primary === 'Admin' || primary === 'SuperAdmin';
  if (!canServerOpen) {
    lines.push({ area: 'Officer app — open accounts', verdict: 'no', detail: 'Needs the Officer role.' });
  } else {
    const blockers: string[] = [];
    if (!hasOperator) blockers.push('Core Director initials and operator number must be on file');
    if (!newAcctTab) {
      blockers.push(
        primary
          ? `their main role in the officer app resolves to ${primary}, which has no New Account tab (the officer app ranks ${primary} above Officer)`
          : 'none of their role spellings is recognised by the officer app, so it falls back to a restricted view',
      );
    }
    if (routedToSandbox) {
      lines.push({
        area: 'Officer app — open accounts',
        verdict: blockers.length ? 'partial' : 'yes',
        detail: `Yes, but every cart goes to Sandbox (Sandbox mode is on) — nothing reaches Core Director.${blockers.length ? ' Also: ' + blockers.join('; ') + '.' : ''}`,
      });
    } else {
      lines.push({
        area: 'Officer app — open accounts',
        verdict: blockers.length ? 'partial' : 'yes',
        detail: blockers.length ? `Officer role held, but ${blockers.join('; ')}.` : 'Yes — real accounts in Core Director.',
      });
    }
  }
  if (hasCi('Supervisor', 'Manager', 'Executive', 'Admin') || hasExact('SuperAdmin')) {
    const bits: string[] = [];
    if (hasCi('Supervisor')) bits.push('approvals and group carts');
    if (hasCi('Manager')) bits.push('regional carts and metrics');
    if (hasCi('Executive')) bits.push('reporting');
    if (hasCi('Admin', 'SuperAdmin')) bits.push('Admin screens and all carts');
    lines.push({ area: 'Officer app — oversight', verdict: 'yes', detail: bits.join('; ') + '.' });
  }

  // Risk panel
  if (hasExact('RiskManager', 'SuperAdmin')) {
    lines.push({ area: 'Risk panel', verdict: signIn ? 'partial' : 'yes', detail: signIn ?? 'Yes.' });
  } else if (hasCi('RiskManager', 'SuperAdmin')) {
    lines.push({ area: 'Risk panel', verdict: 'no', detail: 'Role is stored in a spelling the Risk panel does not recognise — remove it and grant it again here to fix.' });
  } else {
    lines.push({ area: 'Risk panel', verdict: 'no', detail: hasCi('Risk') ? 'Risk Officer alone does not open it — needs Risk Admin.' : 'Needs Risk Admin.' });
  }
  if (hasCi('DisclosureManager')) {
    lines.push({
      area: 'Disclosure approvals',
      verdict: hasCi('Risk') ? 'yes' : 'partial',
      detail: hasCi('Risk') ? 'Can approve and activate disclosure versions.' : 'Held without Risk Officer — the server normally refuses that combination.',
    });
  }

  // SysOps panel
  lines.push(
    hasExact('SystemOps', 'SuperAdmin')
      ? { area: 'SysOps panel', verdict: signIn ? 'partial' : 'yes', detail: signIn ?? 'Yes.' }
      : { area: 'SysOps panel', verdict: 'no', detail: hasCi('SystemOps', 'SuperAdmin') ? 'Stored in a spelling the SysOps panel does not recognise — remove and grant again.' : 'Needs SysOps.' },
  );

  // Admin panel
  const adminPanel = hasExact('SuperAdmin', 'Admin', 'RetailAdmin', 'SysOpsManager', 'RiskManager', 'DisclosureManager');
  lines.push(
    adminPanel
      ? {
          area: 'Admin panel (this site)',
          verdict: signIn ? 'partial' : 'yes',
          detail: signIn ?? (hasExact('SuperAdmin', 'Admin') ? 'Yes — all areas their role allows.' : 'Yes — only the Risk/Documents areas.'),
        }
      : { area: 'Admin panel (this site)', verdict: 'no', detail: hasCi('SuperAdmin', 'Admin') ? 'Stored in a spelling this panel does not recognise — remove and grant again.' : 'No admin role.' },
  );

  return lines;
}

/** Order adds so prerequisites go first (Risk before DisclosureManager). */
export function orderAdds(codes: ReadonlyArray<string>): string[] {
  return [...codes].sort((a, b) => rank(a) - rank(b));
  function rank(c: string): number { return c.toLowerCase() === 'risk' ? 0 : c.toLowerCase() === 'disclosuremanager' ? 2 : 1 }
}

/** Order removes so dependants go first (DisclosureManager before Risk). */
export function orderRemoves(codes: ReadonlyArray<string>): string[] {
  return [...codes].sort((a, b) => rank(a) - rank(b));
  function rank(c: string): number { return c.toLowerCase() === 'disclosuremanager' ? 0 : c.toLowerCase() === 'risk' ? 2 : 1 }
}

/** Short plain-language name for an audit action. */
export function auditActionLabel(action: string): string {
  switch (action) {
    case 'RoleGranted': return 'Role granted';
    case 'RoleRemoved': return 'Role removed';
    case 'UserCreated': return 'Added';
    case 'UserUpdated': return 'Changed';
    case 'UserDeleted': return 'Deleted';
    case 'ProductCategoriesChanged': return 'Product categories';
    case 'ToursReset': return 'Guided tours reset';
    default: return action;
  }
}
